
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SubmitButton } from '@/components/auth/buttons/SubmitButton';
import { PasswordInputField } from '@/components/auth/password/PasswordInputField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ResendVerificationButton } from './ResendVerificationButton';
import { supabase } from '@/integrations/supabase/client';

// Form schema
const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  justVerified?: boolean;
  from?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ justVerified = false, from = '/dashboard' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Get the redirect path from props or location state
  const redirectPath = from || (location.state as { from?: string })?.from || '/dashboard';

  // Log information about the form for debugging
  useEffect(() => {
    console.log("LoginForm component - Initialized");
    console.log("LoginForm redirect path:", redirectPath);
    console.log("LoginForm justVerified:", justVerified);
  }, [redirectPath, justVerified]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    try {
      setIsSubmitting(true);
      setLoginError(null);
      setShowResendVerification(false);
      
      console.log("LoginForm - Attempting login with:", values.email);
      
      // Use the auth context login function which has strict verification checks
      const result = await login(values.email, values.password);
      
      if (!result.success) {
        console.error("LoginForm - Login failed:", result.error);
        setUserEmail(values.email);
        setLoginError(result.error || 'Login failed. Please try again.');
        
        // Show resend verification if it's a verification issue
        if (result.error?.toLowerCase().includes('verification') || 
            result.error?.toLowerCase().includes('verify') ||
            (result as any).needsVerification) {
          setShowResendVerification(true);
          setUserEmail((result as any).userEmail || values.email);
        }
        
        return;
      }
      
      // Login successful - the welcome message is handled in AuthProvider
      console.log("LoginForm - Login successful, redirecting to:", redirectPath);
      
      // Small delay to ensure welcome message is shown before redirect
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 200);
      
    } catch (error: any) {
      console.error('LoginForm - Login error:', error);
      setLoginError(error.message || 'An unexpected error occurred');
      setUserEmail(values.email);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card/95 backdrop-blur-sm p-6 rounded-lg shadow-xl border">
      {loginError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{loginError}</AlertDescription>
        </Alert>
      )}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="email@example.com" 
                    type="email" 
                    autoComplete="email" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInputField
                    field={field}
                    label="Password"
                    autoComplete="current-password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="flex justify-end">
            <Link 
              to="/forgot-password" 
              className="text-sm text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          
          <SubmitButton 
            isLoading={isSubmitting} 
            text="Sign In" 
            loadingText="Signing In..." 
            icon={null}
          />
          
          {showResendVerification && userEmail && (
            <div className="text-center space-y-2 mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Email verification required
              </p>
              <p className="text-sm text-red-700">
                You cannot sign in until you verify your email address. 
                Please check your email for a verification link.
              </p>
              <p className="text-xs text-red-600 mt-2">
                Didn't receive the verification email?
              </p>
              <ResendVerificationButton 
                email={userEmail}
                className="w-full mt-2"
              />
            </div>
          )}
          
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline">
                Create an account
              </Link>
            </p>
          </div>
        </form>
      </Form>
    </div>
  );
};
